import Phaser from 'phaser';
import { TILE_SIZE, UNDERGROUND_LEVEL, RoomBox } from '@blockworld/shared';
import { getGlyphColor } from '../../toki/TokiPonaData';
import type { ProcessedTextures } from '../../rendering/TextureManager';
import type { SpeechBubble } from '../../ui/SpeechBubble';
import { atlasCreateImage, atlasApplyTexture, isAtlasFrame } from '../../rendering/AtlasUtils';

// ============================================================
// SKYBOX CONSTELLATION SPIRAL
// ============================================================
// 11x11 spiral of 120 glyph/constellation indices around 1 empty center (-1).
// Row-major order, spiraling outward from center: 0 -> 119.
// prettier-ignore
const SKYBOX_SPIRAL: number[] = [
    109, 110, 111, 112, 113, 114, 115, 116, 117, 118, 119,
    108,  71,  72,  73,  74,  75,  76,  77,  78,  79,  80,
    107,  70,  41,  42,  43,  44,  45,  46,  47,  48,  81,
    106,  69,  40,  19,  20,  21,  22,  23,  24,  49,  82,
    105,  68,  39,  18,   5,   6,   7,   8,  25,  50,  83,
    104,  67,  38,  17,   4,  -1,   0,   9,  26,  51,  84,
    103,  66,  37,  16,   3,   2,   1,  10,  27,  52,  85,
    102,  65,  36,  15,  14,  13,  12,  11,  28,  53,  86,
    101,  64,  35,  34,  33,  32,  31,  30,  29,  54,  87,
    100,  63,  62,  61,  60,  59,  58,  57,  56,  55,  88,
     99,  98,  97,  96,  95,  94,  93,  92,  91,  90,  89,
];

// Lookup: glyph index → { col, row } in the 11x11 spiral grid
const GLYPH_SPIRAL_POS: Map<number, { col: number; row: number }> = new Map();
for (let i = 0; i < SKYBOX_SPIRAL.length; i++) {
    const g = SKYBOX_SPIRAL[i];
    if (g >= 0) GLYPH_SPIRAL_POS.set(g, { col: i % 11, row: Math.floor(i / 11) });
}

/**
 * SkyboxRenderer — owns all constellation/skybox rendering, shooting star,
 * twinkle sweep, glimmer effects, room box occlusion, and music management.
 *
 * Extracted from GameScene PR A-1.
 */
export class SkyboxRenderer {
    private scene: Phaser.Scene;
    private processedTextures: ProcessedTextures;

    // Skybox (constellation tiled background for night/online mode)
    private skyboxSprites: Phaser.GameObjects.Image[] | null = null;
    private skyboxStartTime: number = 0;
    private skyboxCellTextures: boolean = false; // true once per-cell textures are generated
    private constellationOverlays: Phaser.GameObjects.Image[] = []; // pooled overlay sprites

    // Room boxes — solid black rectangles behind tiles, in front of constellations
    private roomBoxGraphics: Phaser.GameObjects.Graphics | null = null;

    // Home music (plays after start menu is dismissed)
    private homeMusic: Phaser.Sound.BaseSound | null = null;
    private homeMusicPending: boolean = false; // true when title dismissed but audio not yet loaded

    // Night music (plays while connected to multiplayer server)
    private nightMusic: Phaser.Sound.BaseSound | null = null;
    private nightMusicLoaded: boolean = false;

    // Shooting star effect — sprite tile that flies across the sky during night song
    private shootingStarSprite: Phaser.GameObjects.Image | null = null;
    private shootingStarActive: boolean = false;
    private shootingStarX: number = 0;
    private shootingStarY: number = 0;
    private shootingStarLastTick: number = 0;
    // Trigger times in seconds within the night music loop, with Y-offset in px from base
    private static readonly SHOOTING_STAR_TRIGGERS: { time: number; yOffset: number }[] = [
        { time: 165, yOffset: 0 },   // 2:45
        { time: 188, yOffset: 16 },  // 3:08 — 16px lower
    ];
    private shootingStarTriggered: Set<number> = new Set();

    // Twinkle sweep — diagonal band of white stars sweeping bottom-left to top-right
    private static readonly TWINKLE_SWEEP_TRIGGERS: number[] = [
        32.167, 41.167, 65.167, 97.167,
        165.167, 167.967, 170.767, 173.567, 176.367, 179.167, 181.967, 184.767, 187.567, // 9x every 2.8s from ~2:45
        199.167,
    ];
    private twinkleSweepActive: boolean = false;
    private twinkleSweepStartTick: number = 0;
    private twinkleSweepTriggered: Set<number> = new Set();
    private twinkleOverlays: Phaser.GameObjects.Image[] = [];

    // Glimmer — a poem's glyphs glow white in sequence (100ms each), with plus-sign star burst
    // Each entry: [triggerTime, poemGlyphIndices]
    // sewi=90 li=39 awen=8 mama=49 mi=52 lukin=46 jan=17 kama=22 suno=100 lili=40
    // sama=86 sona=97 ala=2 e=9 nimi=66 taso=104 ale=4 ona=70 toki=108 kute=32
    // tenpo=107 ken=24 suli=99 ni=65 pona=84 weka=118 tan=103
    private static readonly GLIMMER_POEMS: { time: number; glyphs: number[] }[] = [
        { time: 121, glyphs: [49,52,46,9,90,17,22,39,97,100,40,8] },          // 2:01
        { time: 132, glyphs: [100,39,97,2,9,66,52,104,17,4,24,46,70] },       // 2:12
        { time: 165, glyphs: [17,4,32,9,90,104,70,39,108,2] },                // 2:45
        { time: 188, glyphs: [107,39,118,9,4,104,90,8,100,86] },              // 3:08
        { time: 231, glyphs: [52,40,103,90,99,65,39,84] },                    // 3:51
    ];
    private glimmerActive: boolean = false;
    private glimmerPoemIndex: number = 0;
    private glimmerStartTick: number = 0;
    private glimmerTriggered: Set<number> = new Set();
    private glimmerOverlays: Phaser.GameObjects.Image[] = [];
    private glimmerActiveGlyph: number = -1; // current glowing glyph index, exposed for twinkle override

    constructor(scene: Phaser.Scene, processedTextures: ProcessedTextures) {
        this.scene = scene;
        this.processedTextures = processedTextures;
    }

    /** True when night music audio file has finished loading. Set by GameScene after audio load completes. */
    setNightMusicLoaded(): void {
        this.nightMusicLoaded = true;
    }

    /** True if skybox sprites are currently active (night mode). */
    get isNightMode(): boolean {
        return this.skyboxSprites !== null;
    }

    /**
     * Load pre-baked skybox textures from the atlas.
     * The skybox + constellation overlays are generated at build time by scripts/prebake_skybox.py
     * and packed into the atlas by scripts/pack_sprites.py.
     */
    generateTexture(): void {
        const MUN_INDEX = 58;

        // Main skybox texture: use atlas frame directly (registered as 'skybox' in atlas)
        // No runtime generation needed — it's pre-baked.

        // Register constellation cell + white-star overlay atlas frames.
        // The atlas already contains skybox_cell_N and skybox_white_N frames.
        // Phaser can use them directly via the atlas key — no extraction needed.
        this.skyboxCellTextures = true;
    }

    /**
     * Update sky/background color based on game mode.
     * - Offline (single player): Blue sky (0x87CEEB)
     * - Online (server): Black sky (0x000000) with constellation skybox
     */
    updateSkyColor(isNight: boolean): void {
        const skyColor = isNight ? 0x000000 : 0x87CEEB;
        this.scene.cameras.main.setBackgroundColor(skyColor);

        if (isNight && isAtlasFrame(this.scene, 'skybox') && !this.skyboxSprites) {
            // 4 copies in a 2x2 grid to cover viewport from any scroll offset
            this.skyboxSprites = [];
            this.skyboxStartTime = Date.now();
            for (let i = 0; i < 4; i++) {
                const sprite = atlasCreateImage(this.scene, 0, 0, 'skybox');
                sprite.setOrigin(0, 0);
                sprite.setDepth(-1);
                this.skyboxSprites.push(sprite);
            }
        }
        if (!isNight && this.skyboxSprites) {
            for (const sprite of this.skyboxSprites) sprite.destroy();
            this.skyboxSprites = null;
            for (const s of this.constellationOverlays) s.destroy();
            this.constellationOverlays = [];
            for (const s of this.twinkleOverlays) s.destroy();
            this.twinkleOverlays = [];
            for (const s of this.glimmerOverlays) s.destroy();
            this.glimmerOverlays = [];
        }
    }

    /** Show or hide the skybox sprites (used during death animation). */
    setSkyboxVisible(visible: boolean): void {
        if (this.skyboxSprites) {
            for (const s of this.skyboxSprites) s.setVisible(visible);
        }
    }

    // ============================================================
    // MUSIC
    // ============================================================

    startHomeMusic(): void {
        this.stopHomeMusic();
        if (!this.scene.cache.audio.exists('home_music')) {
            // Audio still loading — will auto-start when setHomeMusicLoaded() fires
            this.homeMusicPending = true;
            return;
        }
        this.homeMusicPending = false;
        this.homeMusic = this.scene.sound.add('home_music');
        this.homeMusic.play({ loop: true, volume: 2.0 });
    }

    /** Called by GameScene when home_music finishes background loading. */
    setHomeMusicLoaded(): void {
        if (this.homeMusicPending) {
            this.startHomeMusic();
        }
    }

    stopHomeMusic(): void {
        this.homeMusicPending = false;
        if (this.homeMusic) {
            this.homeMusic.stop();
            this.homeMusic.destroy();
            this.homeMusic = null;
        }
    }

    startNightMusic(isOnline: boolean): void {
        if (!isOnline) return;

        // Stop any existing night music first
        this.stopNightMusic();

        // Night music loads in the background — skip if not ready yet
        if (!this.nightMusicLoaded) return;

        this.nightMusic = this.scene.sound.add('night_music');
        this.nightMusic.play({ loop: true, volume: 0.2 });
    }

    stopNightMusic(): void {
        if (this.nightMusic) {
            this.nightMusic.stop();
            this.nightMusic.destroy();
            this.nightMusic = null;
        }
        // Clean up shooting star when music stops
        if (this.shootingStarSprite) {
            this.shootingStarSprite.destroy();
            this.shootingStarSprite = null;
        }
        this.shootingStarActive = false;
        this.shootingStarTriggered.clear();
        // Clean up twinkle sweep
        this.twinkleSweepActive = false;
        this.twinkleSweepTriggered.clear();
        // Clean up glimmer
        this.glimmerActive = false;
        this.glimmerActiveGlyph = -1;
        this.glimmerTriggered.clear();
    }

    // ============================================================
    // RENDER FRAME
    // ============================================================

    /**
     * Called every frame from GameScene.render(). Handles skybox parallax,
     * constellation overlays, shooting star, twinkle sweep, glimmer, and room boxes.
     */
    renderFrame(
        now: number,
        playerX: number,
        playerY: number,
        offsetX: number,
        offsetY: number,
        roomBoxes: RoomBox[],
        localSpeechBubble: SpeechBubble | null,
        otherPlayerSpeechBubbles: Map<string, SpeechBubble>,
    ): void {
        // Parallax scroll the constellation skybox (wraps at 352px tile boundary)
        if (this.skyboxSprites) {
            const SKYBOX_PX = 352; // 88 * 4
            // Offset so moon (glyph 58, cell left 192, center-y 304) starts at left edge, vertically centered
            // Drift up+right at 1px per 3 seconds
            const drift = Math.floor((now - this.skyboxStartTime) / 3000);
            const px = Math.floor(playerX * 0.5) + 192 - drift;
            const py = Math.floor(playerY * 0.5) + 224 + drift;
            const baseX = -(((px % SKYBOX_PX) + SKYBOX_PX) % SKYBOX_PX);
            const baseY = -(((py % SKYBOX_PX) + SKYBOX_PX) % SKYBOX_PX);
            // 2x2 grid of skybox copies for seamless coverage
            this.skyboxSprites[0].setPosition(baseX, baseY);
            this.skyboxSprites[1].setPosition(baseX + SKYBOX_PX, baseY);
            this.skyboxSprites[2].setPosition(baseX, baseY + SKYBOX_PX);
            this.skyboxSprites[3].setPosition(baseX + SKYBOX_PX, baseY + SKYBOX_PX);

            // Constellation speech overlays — color active glyphs
            this.updateConstellationOverlays(baseX, baseY, SKYBOX_PX, localSpeechBubble, otherPlayerSpeechBubbles);

            // Shooting star effect — timed to night music
            this.updateShootingStar(now);

            // Glimmer — poem glyph sequence (runs before twinkle so it sets glimmerActiveGlyph)
            this.updateGlimmer(now, baseX, baseY, SKYBOX_PX);

            // Twinkle sweep — diagonal band of white stars
            this.updateTwinkleSweep(now, baseX, baseY, SKYBOX_PX);
        }

        // Room boxes + underground darkness — solid black, in front of constellations
        {
            if (!this.roomBoxGraphics) {
                this.roomBoxGraphics = this.scene.add.graphics();
                this.roomBoxGraphics.setDepth(0); // between skybox (-1) and tiles (10)
            }
            this.roomBoxGraphics.clear();
            this.roomBoxGraphics.fillStyle(0x000000, 1);

            // Room boxes for spawn platform structure
            for (const box of roomBoxes) {
                const sx = box.x1 * TILE_SIZE + offsetX;
                const sy = box.y1 * TILE_SIZE + offsetY;
                const sw = (box.x2 - box.x1 + 1) * TILE_SIZE;
                const sh = (box.y2 - box.y1 + 1) * TILE_SIZE;
                this.roomBoxGraphics.fillRect(sx, sy, sw, sh);
            }

            // Underground darkness: full-width black from worldY=13 to bottom of screen
            const undergroundScreenY = UNDERGROUND_LEVEL * TILE_SIZE + offsetY;
            const screenW = this.scene.cameras.main.width;
            const screenH = this.scene.cameras.main.height;
            if (undergroundScreenY < screenH) {
                const clipY = Math.max(0, undergroundScreenY);
                this.roomBoxGraphics.fillRect(0, clipY, screenW, screenH - clipY);
            }

            this.roomBoxGraphics.setVisible(true);
        }
    }

    // ============================================================
    // SHOOTING STAR
    // ============================================================

    /**
     * Update shooting star effect — an 8x8 sprite that flies right-to-left
     * across the sky at specific moments in the night song.
     */
    private updateShootingStar(now: number): void {
        if (!this.nightMusic || !this.skyboxSprites) return;

        // Get current playback position in seconds
        const sound = this.nightMusic as Phaser.Sound.WebAudioSound;
        if (!sound.seek && sound.seek !== 0) return;
        const seekPos = sound.seek;

        const screenW = this.scene.cameras.main.width;
        const screenH = this.scene.cameras.main.height;
        const baseY = Math.floor(screenH * 0.25);

        // Check if any trigger time is hit (within a 0.5s window to avoid missing)
        for (const trigger of SkyboxRenderer.SHOOTING_STAR_TRIGGERS) {
            if (seekPos >= trigger.time && seekPos < trigger.time + 0.5 && !this.shootingStarTriggered.has(trigger.time)) {
                // Launch a shooting star from the right edge
                this.shootingStarTriggered.add(trigger.time);
                this.shootingStarActive = true;
                this.shootingStarX = screenW;
                this.shootingStarY = baseY + trigger.yOffset;
                this.shootingStarLastTick = Math.floor(now / 100);

                if (!this.shootingStarSprite) {
                    this.shootingStarSprite = atlasCreateImage(this.scene, 0, 0, this.processedTextures.shooting_star);
                    this.shootingStarSprite.setOrigin(0, 0);
                    this.shootingStarSprite.setDepth(-0.8); // in front of stars (-1), behind overlays (-0.5)
                    this.shootingStarSprite.setScrollFactor(0); // fixed to screen, not world
                }
                this.shootingStarSprite.setPosition(this.shootingStarX, this.shootingStarY);
                this.shootingStarSprite.setVisible(true);
            }
        }

        // Reset triggered set when music loops back (seek < smallest trigger time)
        if (seekPos < SkyboxRenderer.SHOOTING_STAR_TRIGGERS[0].time - 1) {
            this.shootingStarTriggered.clear();
        }

        // Move shooting star 1 tile (8px) per 100ms tick, right to left
        if (this.shootingStarActive && this.shootingStarSprite) {
            const currentTick = Math.floor(now / 100);
            if (currentTick > this.shootingStarLastTick) {
                const ticksElapsed = currentTick - this.shootingStarLastTick;
                this.shootingStarX -= TILE_SIZE * ticksElapsed;
                this.shootingStarLastTick = currentTick;
                this.shootingStarSprite.setPosition(this.shootingStarX, this.shootingStarY);
            }

            // Off the left edge — hide and deactivate
            if (this.shootingStarX < -TILE_SIZE) {
                this.shootingStarActive = false;
                this.shootingStarSprite.setVisible(false);
            }
        }
    }

    // ============================================================
    // TWINKLE SWEEP
    // ============================================================

    /**
     * Twinkle sweep — a `/`-shaped diagonal of white stars sweeps from
     * bottom-left to top-right across the viewport, 1 constellation cell per tick.
     */
    private updateTwinkleSweep(now: number, baseX: number, baseY: number, skyboxPx: number): void {
        if (!this.nightMusic || !this.skyboxCellTextures) {
            for (const s of this.twinkleOverlays) s.setVisible(false);
            return;
        }

        const sound = this.nightMusic as Phaser.Sound.WebAudioSound;
        if (!sound.seek && sound.seek !== 0) {
            for (const s of this.twinkleOverlays) s.setVisible(false);
            return;
        }
        const seekPos = sound.seek;

        // Check trigger times
        for (const triggerTime of SkyboxRenderer.TWINKLE_SWEEP_TRIGGERS) {
            if (seekPos >= triggerTime && seekPos < triggerTime + 0.5 && !this.twinkleSweepTriggered.has(triggerTime)) {
                this.twinkleSweepTriggered.add(triggerTime);
                this.twinkleSweepActive = true;
                this.twinkleSweepStartTick = Math.floor(now / 100);
            }
        }

        // Reset triggered set when music loops back
        if (seekPos < SkyboxRenderer.TWINKLE_SWEEP_TRIGGERS[0] - 1) {
            this.twinkleSweepTriggered.clear();
        }

        if (!this.twinkleSweepActive) {
            for (const s of this.twinkleOverlays) s.setVisible(false);
            return;
        }

        const CELL = 32;
        const screenW = this.scene.cameras.main.width;
        const screenH = this.scene.cameras.main.height;
        const viewCols = Math.ceil(screenW / CELL) + 1;
        const viewRows = Math.ceil(screenH / CELL) + 1;
        const totalDiags = viewCols + viewRows - 1;

        const currentTick = Math.floor(now / 100);
        const elapsed = Math.floor((currentTick - this.twinkleSweepStartTick) / 2);

        if (elapsed >= totalDiags) {
            this.twinkleSweepActive = false;
            for (const s of this.twinkleOverlays) s.setVisible(false);
            return;
        }

        // Diagonal parameter: viewRow - viewCol = sweepD
        // Starts at (viewRows-1) for bottom-left, decreases toward -(viewCols-1) for top-right
        const sweepD = (viewRows - 1) - elapsed;

        const MUN = 58;
        const GRID = 11;
        let idx = 0;

        // Check every constellation cell across the 2x2 skybox grid
        const copyOffsets = [[0, 0], [skyboxPx, 0], [0, skyboxPx], [skyboxPx, skyboxPx]];

        for (const [cox, coy] of copyOffsets) {
            for (let grow = 0; grow < GRID; grow++) {
                for (let gcol = 0; gcol < GRID; gcol++) {
                    const glyphIndex = SKYBOX_SPIRAL[grow * GRID + gcol];
                    if (glyphIndex < 0 || glyphIndex === MUN) continue;
                    // Skip glyph currently owned by glimmer effect
                    if (glyphIndex === this.glimmerActiveGlyph) continue;

                    const cellX = baseX + cox + gcol * CELL;
                    const cellY = baseY + coy + grow * CELL;

                    // Skip if off-screen
                    if (cellX + CELL <= 0 || cellX >= screenW || cellY + CELL <= 0 || cellY >= screenH) continue;

                    // Viewport diagonal for this cell (based on cell center)
                    const viewCol = Math.floor((cellX + CELL / 2) / CELL);
                    const viewRow = Math.floor((cellY + CELL / 2) / CELL);
                    if (viewRow - viewCol !== sweepD) continue;

                    const texKey = `skybox_white_${glyphIndex}`;
                    if (!isAtlasFrame(this.scene, texKey)) continue;

                    // Grow overlay pool as needed
                    while (this.twinkleOverlays.length <= idx) {
                        const s = this.scene.add.image(0, 0, '__DEFAULT');
                        s.setOrigin(0, 0);
                        s.setDepth(-0.5);
                        s.setVisible(false);
                        this.twinkleOverlays.push(s);
                    }

                    const s = this.twinkleOverlays[idx++];
                    atlasApplyTexture(this.scene, s, texKey);
                    s.setPosition(cellX, cellY);
                    s.setVisible(true);
                }
            }
        }

        // Hide unused overlays
        for (let i = idx; i < this.twinkleOverlays.length; i++) {
            this.twinkleOverlays[i].setVisible(false);
        }
    }

    // ============================================================
    // GLIMMER
    // ============================================================

    /**
     * Glimmer — step through a poem's glyphs at 100ms each, showing white stars
     * plus the plus-sign burst overlay for the active glyph. Sets glimmerActiveGlyph
     * so the twinkle sweep can skip that cell.
     */
    private updateGlimmer(now: number, baseX: number, baseY: number, skyboxPx: number): void {
        this.glimmerActiveGlyph = -1;

        if (!this.nightMusic || !this.skyboxCellTextures) {
            for (const s of this.glimmerOverlays) s.setVisible(false);
            return;
        }

        const sound = this.nightMusic as Phaser.Sound.WebAudioSound;
        if (!sound.seek && sound.seek !== 0) {
            for (const s of this.glimmerOverlays) s.setVisible(false);
            return;
        }
        const seekPos = sound.seek;

        // Check triggers
        for (let i = 0; i < SkyboxRenderer.GLIMMER_POEMS.length; i++) {
            const t = SkyboxRenderer.GLIMMER_POEMS[i].time;
            if (seekPos >= t && seekPos < t + 0.5 && !this.glimmerTriggered.has(t)) {
                this.glimmerTriggered.add(t);
                this.glimmerActive = true;
                this.glimmerPoemIndex = i;
                this.glimmerStartTick = Math.floor(now / 100);
            }
        }

        // Reset triggered set when music loops back
        if (seekPos < SkyboxRenderer.GLIMMER_POEMS[0].time - 1) {
            this.glimmerTriggered.clear();
        }

        if (!this.glimmerActive) {
            for (const s of this.glimmerOverlays) s.setVisible(false);
            return;
        }

        const poem = SkyboxRenderer.GLIMMER_POEMS[this.glimmerPoemIndex];
        const currentTick = Math.floor(now / 100);
        const step = currentTick - this.glimmerStartTick;

        if (step >= poem.glyphs.length) {
            this.glimmerActive = false;
            for (const s of this.glimmerOverlays) s.setVisible(false);
            return;
        }

        const glyphIndex = poem.glyphs[step];
        const MUN = 58;
        if (glyphIndex < 0 || glyphIndex === MUN) {
            for (const s of this.glimmerOverlays) s.setVisible(false);
            return;
        }

        this.glimmerActiveGlyph = glyphIndex;

        const pos = GLYPH_SPIRAL_POS.get(glyphIndex);
        if (!pos) {
            for (const s of this.glimmerOverlays) s.setVisible(false);
            return;
        }

        const CELL = 32;
        const whiteKey = `skybox_white_${glyphIndex}`;
        const plusKey = `skybox_cell_${glyphIndex}`;
        const hasWhite = isAtlasFrame(this.scene, whiteKey);
        const hasPlus = isAtlasFrame(this.scene, plusKey);
        if (!hasWhite && !hasPlus) {
            for (const s of this.glimmerOverlays) s.setVisible(false);
            return;
        }

        const cellX = pos.col * CELL;
        const cellY = pos.row * CELL;
        const copyOffsets = [[0, 0], [skyboxPx, 0], [0, skyboxPx], [skyboxPx, skyboxPx]];

        // Compute current twinkle diagonal so we know if glimmer+twinkle overlap
        let twinkleSweepD: number | null = null;
        if (this.twinkleSweepActive) {
            const screenW = this.scene.cameras.main.width;
            const screenH = this.scene.cameras.main.height;
            const viewCols = Math.ceil(screenW / CELL) + 1;
            const viewRows = Math.ceil(screenH / CELL) + 1;
            const totalDiags = viewCols + viewRows - 1;
            const twinkleElapsed = Math.floor((currentTick - this.twinkleSweepStartTick) / 2);
            if (twinkleElapsed >= 0 && twinkleElapsed < totalDiags) {
                twinkleSweepD = (viewRows - 1) - twinkleElapsed;
            }
        }

        let idx = 0;

        for (const [ox, oy] of copyOffsets) {
            const screenX = baseX + ox + cellX;
            const screenY = baseY + oy + cellY;

            // Check if this copy falls on the twinkle diagonal
            const onDiagonal = twinkleSweepD !== null &&
                Math.floor((screenY + CELL / 2) / CELL) - Math.floor((screenX + CELL / 2) / CELL) === twinkleSweepD;

            // White star overlay (always)
            if (hasWhite) {
                while (this.glimmerOverlays.length <= idx) {
                    const s = this.scene.add.image(0, 0, '__DEFAULT');
                    s.setOrigin(0, 0);
                    s.setDepth(-0.3);
                    s.setVisible(false);
                    this.glimmerOverlays.push(s);
                }
                const sw = this.glimmerOverlays[idx++];
                atlasApplyTexture(this.scene, sw, whiteKey);
                sw.setPosition(screenX, screenY);
                sw.setVisible(true);
            }
            // Plus-sign burst only when also on twinkle diagonal
            if (hasPlus && onDiagonal) {
                while (this.glimmerOverlays.length <= idx) {
                    const s = this.scene.add.image(0, 0, '__DEFAULT');
                    s.setOrigin(0, 0);
                    s.setDepth(-0.3);
                    s.setVisible(false);
                    this.glimmerOverlays.push(s);
                }
                const sp = this.glimmerOverlays[idx++];
                atlasApplyTexture(this.scene, sp, plusKey);
                sp.setPosition(screenX - 1, screenY - 1);
                sp.setTint(0xffffff);
                sp.setVisible(true);
            }
        }

        for (let i = idx; i < this.glimmerOverlays.length; i++) {
            this.glimmerOverlays[i].setVisible(false);
        }
    }

    // ============================================================
    // CONSTELLATION OVERLAYS
    // ============================================================

    /**
     * Show tinted constellation overlays for glyphs currently being spoken.
     * Collects active glyph indices from all speech bubbles, then positions
     * tinted cell sprites on top of the 2x2 skybox grid.
     */
    private updateConstellationOverlays(
        baseX: number,
        baseY: number,
        skyboxPx: number,
        localSpeechBubble: SpeechBubble | null,
        otherPlayerSpeechBubbles: Map<string, SpeechBubble>,
    ): void {
        if (!this.skyboxCellTextures) {
            // Hide all if cell textures not ready
            for (const s of this.constellationOverlays) s.setVisible(false);
            return;
        }

        // Collect active glyphs from all speech bubbles: Map<glyphIndex, color>
        // Skip glyph 58 (moon) — it has no overlay texture
        const MUN = 58;
        const activeGlyphs: Map<number, number> = new Map();
        if (localSpeechBubble) {
            const gi = localSpeechBubble.getActiveGlyphIndex();
            if (gi >= 0 && gi !== MUN) activeGlyphs.set(gi, getGlyphColor(gi));
        }
        for (const [, bubble] of otherPlayerSpeechBubbles) {
            const gi = bubble.getActiveGlyphIndex();
            if (gi >= 0 && gi !== MUN && !activeGlyphs.has(gi)) activeGlyphs.set(gi, getGlyphColor(gi));
        }

        // Each active glyph needs 4 overlay sprites (one per skybox copy in the 2x2 grid)
        const needed = activeGlyphs.size * 4;

        // Grow pool if needed
        while (this.constellationOverlays.length < needed) {
            const s = this.scene.add.image(0, 0, '__DEFAULT');
            s.setOrigin(0, 0);
            s.setDepth(-0.5); // just above skybox (-1)
            s.setVisible(false);
            this.constellationOverlays.push(s);
        }

        // Hide all first
        for (const s of this.constellationOverlays) s.setVisible(false);

        let idx = 0;
        const CELL = 32; // 8 * 4 pixel stride
        const offsets = [[0, 0], [skyboxPx, 0], [0, skyboxPx], [skyboxPx, skyboxPx]];

        for (const [glyphIndex, color] of activeGlyphs) {
            const pos = GLYPH_SPIRAL_POS.get(glyphIndex);
            if (!pos) continue;
            const texKey = `skybox_cell_${glyphIndex}`;
            if (!isAtlasFrame(this.scene, texKey)) continue;

            const cellX = pos.col * CELL;
            const cellY = pos.row * CELL;

            for (const [ox, oy] of offsets) {
                const s = this.constellationOverlays[idx++];
                atlasApplyTexture(this.scene, s, texKey);
                s.setPosition(baseX + ox + cellX - 1, baseY + oy + cellY - 1);
                s.setTint(color);
                s.setVisible(true);
            }
        }
    }

    destroy(): void {
        this.stopNightMusic();
        this.stopHomeMusic();
        if (this.skyboxSprites) {
            for (const s of this.skyboxSprites) s.destroy();
            this.skyboxSprites = null;
        }
        for (const s of this.constellationOverlays) s.destroy();
        this.constellationOverlays = [];
        for (const s of this.twinkleOverlays) s.destroy();
        this.twinkleOverlays = [];
        for (const s of this.glimmerOverlays) s.destroy();
        this.glimmerOverlays = [];
        if (this.roomBoxGraphics) {
            this.roomBoxGraphics.destroy();
            this.roomBoxGraphics = null;
        }
    }
}
